import React, { useMemo, useState } from 'react';
import {
  Box,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Image,
  Input,
  Link,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { useReduxDispatch } from '../../app/store';
import chargerOfflineImg from '../../assets/images/charger_offline.svg';
import chargerImg from '../../assets/images/evse_charger_station_offline.svg';
import { ArcButton } from '../../common/components/ArcButton';
import { InternetConnectionRecoveryPage } from '../../common/components/InternetConnectionRecoveryPage';
import { LanguageSelector } from '../../common/components/LanguageSelector';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { openPrivacyNoticePdfInWebView } from '../account/settings/privacy/privacy-notice-pdf';
import { openTermsOfUsePdfInWebView } from '../account/terms-of-use-pdf';
import { makePostcodeFormSchema } from '../site/site-helpers';
import { selectSite } from '../site/siteSlice';
import { PostcodeFormData, PostcodeInput } from './PostcodeInput';
import { getSites, selectSites } from './sitesSlice';

export type SiteFormData = {
  name: string;
  address: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
};

type Props = {
  onSubmit: (values: SiteFormData) => void;
  readonly externalError?: string;
};

export function SiteForm({ onSubmit, externalError }: Props) {
  const dispatch = useReduxDispatch();
  const [isRecoveryRetrying, setIsRecoveryRetrying] = useState(false);
  const site = useSelector(selectSite);
  const { sites, status } = useSelector(selectSites);
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const schema = yup.object().shape({
    ...makePostcodeFormSchema(t),
    name: yup.string().max(50).required().label(t('Common.home name')),
  });
  const {
    register,
    control,
    getValues,
    reset,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SiteFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      postCode: site.post_code ?? '',
      name: site.site_name.includes('cIAM') ? '' : site.site_name,
    },
  });
  const backgroundColor = useColorModeValue('#FFFFFF', 'customGrey.900');
  const linkColor = useColorModeValue('schneiderSkyBlue.600', 'schneiderSkyBlue.200');
  const toast = useToast({ isClosable: true });
  const [isOpeningTerms, setIsOpeningTerms] = useState(false);
  const [isOpeningPrivacy, setIsOpeningPrivacy] = useState(false);

  const { isOpen: isLogoutOpen, onOpen: onLogoutOpen, onClose: onLogoutClose } = useDisclosure();

  const urlSearchParamsToGoBack = useMemo(() => {
    const params = new URLSearchParams(search);
    params.set('direction', 'back');
    return params.toString();
  }, [search]);

  const backURL = useMemo(() => {
    if (sites.length === 1) {
      // If a user has one site and a site id isn't in the search params, we know they are creating a new site --
      // -- for the first time. In this case, we want to provide the logout modal (instead of the sites list)
      if (urlSearchParamsToGoBack.includes(`${site.site_id}`)) {
        return undefined;
      }
    }
    return `/sites/list?${urlSearchParamsToGoBack}`;
  }, [sites.length, urlSearchParamsToGoBack, site.site_id]);

  const onClickBack = useMemo(() => {
    if (!backURL) return onLogoutOpen;
    return undefined;
  }, [backURL, onLogoutOpen]);

  function handleSelectLocation(location: PostcodeFormData) {
    const currentValues = getValues();
    reset({
      ...currentValues,
      ...location,
    });
  }

  async function handleRetry() {
    const result = await dispatch(getSites({ pageSize: 2, offset: 0 }));
    if (result.meta.requestStatus === 'fulfilled') {
      return '/';
    }

    return;
  }

  async function handleOpenTermsOfUse() {
    setIsOpeningTerms(true);
    try {
      await openTermsOfUsePdfInWebView(i18n.language);
    } catch (error) {
      console.error('Failed to open Terms of Use PDF:', error);
      toast({
        title: t('Common.something went wrong'),
        description: t('Common.please try again'),
        status: 'error',
      });
    }
    setIsOpeningTerms(false);
  }

  async function handleOpenPrivacyNotice() {
    setIsOpeningPrivacy(true);
    try {
      await openPrivacyNoticePdfInWebView(i18n.language);
    } catch (error) {
      console.error('Failed to open Privacy Notice PDF:', error);
      toast({
        title: t('Common.something went wrong'),
        description: t('Common.please try again'),
        status: 'error',
      });
    }
    setIsOpeningPrivacy(false);
  }

  if (status === 'failed' || isRecoveryRetrying) {
    return (
      <InternetConnectionRecoveryPage
        onRetry={handleRetry}
        onRetryingChange={setIsRecoveryRetrying}
        pageTitle={t('Common.get started')}
      />
    );
  }

  return (
    <SlidingAnimationPageBase
      px={6}
      includeTopNav
      title={t('Common.get started')}
      backURL={backURL}
      onClickBack={onClickBack}
    >
      <Center flexDirection="column">
        <Center my={4}>
          <Image src={chargerOfflineImg} alt="EV Charger Setup" />
          <Center pos="absolute">
            <Image src={chargerImg} alt="EV Charger" />
          </Center>
        </Center>
        <Text fontSize={24} fontWeight={400} textAlign="center">
          {t('Set Address.welcome')}
        </Text>
        <Text textAlign="center">
          {t('Set Address.charging address')} {t('Set Address.location to predict')}
        </Text>
      </Center>
      <Box onSubmit={handleFormSubmit(onSubmit)} as={'form'} data-testid="site-form" mt={8}>
        <PostcodeInput
          control={control}
          error={externalError || errors?.postCode?.message}
          handleSelectLocation={handleSelectLocation}
        />

        <FormControl mt={3} isInvalid={!!errors.name}>
          <Input
            {...register('name')}
            borderRadius={0}
            height="50px"
            type="text"
            data-private
            autoComplete="off"
            data-testid="site-home-name"
            background={backgroundColor}
            borderColor={'customGrey.400'}
            placeholder={`${t('Common.home name')}...`}
          />
          <FormHelperText mb={1}>{t('Common.home name example')}</FormHelperText>
          <FormErrorMessage data-testid="site-name-error">{errors?.name?.message}</FormErrorMessage>
        </FormControl>

        <LanguageSelector mt={4} />

        <Center flexDirection="column">
          <ArcButton
            isLoading={isSubmitting}
            mt={8}
            type="submit"
            data-testid="save-button"
            w={'100%'}
            arcColor="#3DCD57"
          >
            {t('Common.continue')}
          </ArcButton>
          <Text mx={8} mt={4} mb={6} fontSize={13} textAlign="center">
            {t('Set Address.by clicking continue')}{' '}
            <Link
              fontWeight={600}
              data-testid="privacy-notice-link"
              color={linkColor}
              onClick={handleOpenPrivacyNotice}
              opacity={isOpeningPrivacy ? 0.6 : 1}
              pointerEvents={isOpeningPrivacy ? 'none' : 'auto'}
            >
              {t('Privacy Notice.privacy notice')}
            </Link>{' '}
            {t('Set Address.and')}{' '}
            <Link
              fontWeight={600}
              data-testid="terms-of-use-link"
              color={linkColor}
              onClick={handleOpenTermsOfUse}
              opacity={isOpeningTerms ? 0.6 : 1}
              pointerEvents={isOpeningTerms ? 'none' : 'auto'}
            >
              {t('Common.terms of use')}
            </Link>
          </Text>
        </Center>
      </Box>
      <AlertDialogModal
        isOpen={isLogoutOpen}
        onClose={onLogoutClose}
        header={t('Account.logout')}
        subHeader={t('Account.are you sure logout')}
        confirmButtonName={t('Account.logout')}
        onConfirm={() => navigate('/logout')}
        cancelButtonName={t('Common.cancel')}
      />
    </SlidingAnimationPageBase>
  );
}
